import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { AdminShell } from "@/components/admin/admin-shell";
import { requireSchoolAdminOrOnboard, useSchoolContext } from "@/lib/school-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import {
  Loader2, ShieldCheck, Sparkles, AlertTriangle, CheckCircle2, XCircle,
  RefreshCw, FileSearch, Copy, Link2, Gauge,
} from "lucide-react";
import { toast } from "sonner";
import {
  reviewContentQuality, listQualityReviews, decideQualityReview, listUnreviewedContent,
  QUALITY_CHECKS, type QualityCheck, type QualitySuggestion,
} from "@/lib/content-quality.functions";

export const Route = createFileRoute("/admin/content-quality")({
  ssr: false,
  beforeLoad: () => requireSchoolAdminOrOnboard(),
  head: () => ({
    meta: [
      { title: "AI Content Quality · MAKAI Admin" },
      { name: "description", content: "Automatically review uploaded educational content for curriculum alignment, language quality, safety, bias, accessibility and copyright risk, then approve it for the school or marketplace." },
      { property: "og:title", content: "AI Content Quality · MAKAI Admin" },
      { property: "og:description", content: "Quality scores, improvement suggestions and approval workflows for all school and marketplace content." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Page,
});

type ReviewRow = {
  id: string; title: string; content_kind: string; content_id: string | null; scope: string;
  overall_score: number; status: string; checks: unknown; suggestions: unknown; flags: string[];
  summary: string | null; duplicate_of: string | null; duplicate_similarity: number | null;
  created_at: string; decision_note: string | null; reviewed_at: string | null;
};

function scoreTone(score: number) {
  if (score >= 85) return "text-emerald-600";
  if (score >= 70) return "text-amber-600";
  return "text-destructive";
}

const SEVERITY_VARIANT: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  pass: "secondary", minor: "outline", major: "default", critical: "destructive",
};

const STATUS_LABEL: Record<string, string> = {
  pending: "Awaiting approval",
  needs_changes: "Needs changes",
  approved: "Approved",
  rejected: "Rejected",
  reviewing: "Reviewing",
};

function Page() {
  const { active } = useSchoolContext();
  const schoolId = active?.school_id ?? "";

  const [statusFilter, setStatusFilter] = useState("all");
  const [scopeFilter, setScopeFilter] = useState("all");

  const listFn = useServerFn(listQualityReviews);
  const queueFn = useServerFn(listUnreviewedContent);

  const reviews = useQuery({
    queryKey: ["cq-reviews", schoolId, statusFilter, scopeFilter],
    enabled: !!schoolId,
    queryFn: () => listFn({ data: { schoolId, status: statusFilter as never, scope: scopeFilter as never, limit: 100 } }),
  });

  const queue = useQuery({
    queryKey: ["cq-queue", schoolId],
    enabled: !!schoolId,
    queryFn: () => queueFn({ data: { schoolId, limit: 40 } }),
  });

  const rows = (reviews.data ?? []) as unknown as ReviewRow[];
  const stats = useMemo(() => {
    const total = rows.length;
    const pending = rows.filter((r) => r.status === "pending").length;
    const blocked = rows.filter((r) => r.status === "needs_changes" || r.status === "rejected").length;
    const avg = total ? Math.round(rows.reduce((s, r) => s + r.overall_score, 0) / total) : 0;
    return { total, pending, blocked, avg };
  }, [rows]);

  return (
    <AdminShell
      title="AI Content Quality"
      subtitle="Every upload is checked, scored and approved before learners see it"
      action={
        <div className="flex gap-2">
          <ManualReviewDialog schoolId={schoolId} onDone={() => { reviews.refetch(); queue.refetch(); }} />
          <Button variant="outline" size="sm" onClick={() => { reviews.refetch(); queue.refetch(); }}>
            <RefreshCw className="mr-1.5 h-4 w-4" />Refresh
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="grid gap-3 sm:grid-cols-4">
          <StatCard icon={FileSearch} label="Reviewed items" value={stats.total} />
          <StatCard icon={Gauge} label="Average score" value={`${stats.avg}`} tone={scoreTone(stats.avg)} />
          <StatCard icon={ShieldCheck} label="Awaiting approval" value={stats.pending} />
          <StatCard icon={AlertTriangle} label="Blocked / needs work" value={stats.blocked} />
        </div>

        <Tabs defaultValue="reviews">
          <TabsList>
            <TabsTrigger value="reviews">Reviews</TabsTrigger>
            <TabsTrigger value="queue">Unreviewed uploads ({queue.data?.length ?? 0})</TabsTrigger>
            <TabsTrigger value="checks">What we check</TabsTrigger>
          </TabsList>

          {/* -------------------- reviews -------------------- */}
          <TabsContent value="reviews" className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[190px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="pending">Awaiting approval</SelectItem>
                  <SelectItem value="needs_changes">Needs changes</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Select value={scopeFilter} onValueChange={setScopeFilter}>
                <SelectTrigger className="w-[190px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">School + marketplace</SelectItem>
                  <SelectItem value="school">School only</SelectItem>
                  <SelectItem value="marketplace">Marketplace only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {reviews.isLoading ? (
              <Card><CardContent className="py-12 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></CardContent></Card>
            ) : !rows.length ? (
              <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
                No content has been reviewed yet. Run a review from the “Unreviewed uploads” tab.
              </CardContent></Card>
            ) : (
              rows.map((r) => <ReviewCard key={r.id} row={r} onDecided={() => reviews.refetch()} />)
            )}
          </TabsContent>

          {/* -------------------- queue -------------------- */}
          <TabsContent value="queue" className="space-y-3">
            {queue.isLoading ? (
              <Card><CardContent className="py-12 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></CardContent></Card>
            ) : !queue.data?.length ? (
              <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
                Everything uploaded to this school has been reviewed. 🎉
              </CardContent></Card>
            ) : (
              queue.data.map((item) => (
                <QueueRow
                  key={item.id}
                  schoolId={schoolId}
                  item={item}
                  onDone={() => { queue.refetch(); reviews.refetch(); }}
                />
              ))
            )}
          </TabsContent>

          {/* -------------------- checks -------------------- */}
          <TabsContent value="checks">
            <Card>
              <CardHeader><CardTitle className="text-sm">Automatic checks on every upload</CardTitle></CardHeader>
              <CardContent className="grid gap-2 sm:grid-cols-2">
                {QUALITY_CHECKS.map((c) => (
                  <div key={c.key} className="flex items-start gap-2 rounded-lg border p-3 text-sm">
                    <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <div>
                      <p className="font-medium">{c.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {c.ai ? "Reviewed by MAKAI AI against the class and subject." : "Checked automatically by the platform."}
                      </p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminShell>
  );
}

/* ------------------------------------------------------------------ */

function StatCard({
  icon: Icon, label, value, tone,
}: { icon: typeof ShieldCheck; label: string; value: string | number; tone?: string }) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 py-4">
        <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className={`text-xl font-semibold ${tone ?? ""}`}>{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function QueueRow({
  schoolId, item, onDone,
}: {
  schoolId: string;
  item: { id: string; kind: string; title: string; subtitle: string; text: string; media: string[]; gradeLevel?: string; language?: string };
  onDone: () => void;
}) {
  const run = useServerFn(reviewContentQuality);
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState<"school" | "marketplace">("school");

  async function review() {
    if (!item.text.trim()) { toast.error("This item has no text to review."); return; }
    setBusy(true);
    const t = toast.loading(`Reviewing “${item.title}”…`);
    try {
      const r = await run({
        data: {
          schoolId,
          contentKind: item.kind as never,
          contentId: item.id,
          title: item.title,
          scope,
          text: item.text,
          mediaUrls: item.media.slice(0, 12),
          hasAltText: false,
          gradeLevel: item.gradeLevel,
          language: item.language,
        },
      });
      toast.success(`Score ${r.overall_score}/100 — ${STATUS_LABEL[r.status] ?? r.status}`, { id: t });
      onDone();
    } catch (e) {
      toast.error((e as Error).message, { id: t });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-3 py-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{item.title}</p>
          <p className="text-xs text-muted-foreground">
            {item.subtitle}{item.gradeLevel ? ` · ${item.gradeLevel}` : ""} · {item.media.length} media link(s)
          </p>
        </div>
        <Select value={scope} onValueChange={(v) => setScope(v as never)}>
          <SelectTrigger className="h-8 w-[150px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="school">School content</SelectItem>
            <SelectItem value="marketplace">Marketplace</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" onClick={review} disabled={busy}>
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
          Review
        </Button>
      </CardContent>
    </Card>
  );
}

function ReviewCard({ row, onDecided }: { row: ReviewRow; onDecided: () => void }) {
  const decide = useServerFn(decideQualityReview);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const checks = (Array.isArray(row.checks) ? row.checks : []) as QualityCheck[];
  const suggestions = (Array.isArray(row.suggestions) ? row.suggestions : []) as QualitySuggestion[];

  async function send(decision: "approved" | "rejected" | "needs_changes") {
    setBusy(true);
    const t = toast.loading("Saving decision…");
    try {
      await decide({ data: { id: row.id, decision, note: note || undefined } });
      toast.success(`Marked ${STATUS_LABEL[decision] ?? decision}`, { id: t });
      onDecided();
    } catch (e) {
      toast.error((e as Error).message, { id: t });
    } finally {
      setBusy(false);
    }
  }

  const blocked = checks.some((c) => c.severity === "critical");

  return (
    <Card className={blocked ? "border-destructive/40" : ""}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base">{row.title}</CardTitle>
            <div className="mt-2 flex flex-wrap gap-1">
              <Badge variant="secondary">{row.content_kind.replace(/_/g, " ")}</Badge>
              <Badge variant="outline">{row.scope}</Badge>
              <Badge variant={row.status === "approved" ? "default" : row.status === "rejected" ? "destructive" : "outline"}>
                {STATUS_LABEL[row.status] ?? row.status}
              </Badge>
              {row.duplicate_of && (
                <Badge variant="destructive"><Copy className="mr-1 h-3 w-3" />
                  Possible duplicate {row.duplicate_similarity ? `(${Math.round(row.duplicate_similarity * 100)}%)` : ""}
                </Badge>
              )}
              {row.flags?.slice(0, 4).map((f) => <Badge key={f} variant="outline">{f.replace(/_/g, " ")}</Badge>)}
            </div>
          </div>
          <div className="text-right">
            <p className={`text-3xl font-bold ${scoreTone(row.overall_score)}`}>{row.overall_score}</p>
            <p className="text-xs text-muted-foreground">quality score</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {row.summary && <p className="text-sm text-muted-foreground">{row.summary}</p>}

        <div className="grid gap-2 sm:grid-cols-2">
          {checks.map((c) => (
            <div key={c.key} className="rounded-lg border p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium">{c.label}</p>
                <Badge variant={SEVERITY_VARIANT[c.severity] ?? "outline"}>{c.severity}</Badge>
              </div>
              <Progress value={c.score} className="my-2 h-1.5" />
              <p className="text-xs text-muted-foreground">{c.summary}</p>
              {!!c.issues?.length && (
                <ul className="mt-2 list-disc pl-4 text-xs text-muted-foreground">
                  {c.issues.slice(0, 5).map((i, j) => <li key={j}>{i}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>

        {!!suggestions.length && (
          <Accordion type="single" collapsible>
            <AccordionItem value="s">
              <AccordionTrigger className="text-sm">Suggested improvements ({suggestions.length})</AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-2 text-sm">
                  {suggestions.map((s, i) => (
                    <li key={i} className="flex gap-2">
                      <Badge variant="outline" className="h-fit shrink-0">{s.priority}</Badge>
                      <span><span className="text-muted-foreground">{s.check.replace(/_/g, " ")}: </span>{s.suggestion}</span>
                    </li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        <Separator />
        <div className="flex flex-wrap items-center gap-2">
          <Input
            className="h-9 min-w-[220px] flex-1"
            placeholder="Decision note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button size="sm" variant="outline" disabled={busy} onClick={() => send("needs_changes")}>
            <AlertTriangle className="mr-1.5 h-4 w-4" />Request changes
          </Button>
          <Button size="sm" variant="destructive" disabled={busy} onClick={() => send("rejected")}>
            <XCircle className="mr-1.5 h-4 w-4" />Reject
          </Button>
          <Button size="sm" disabled={busy || blocked} onClick={() => send("approved")}>
            <CheckCircle2 className="mr-1.5 h-4 w-4" />Approve
          </Button>
        </div>
        {blocked && (
          <p className="text-xs text-destructive">
            Approval is blocked while a critical issue is open. Fix the content and re-run the review.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */

function ManualReviewDialog({ schoolId, onDone }: { schoolId: string; onDone: () => void }) {
  const run = useServerFn(reviewContentQuality);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [scope, setScope] = useState<"school" | "marketplace">("school");
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [media, setMedia] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!schoolId) { toast.error("Pick a school first."); return; }
    if (title.trim().length < 2 || text.trim().length < 20) {
      toast.error("Add a title and at least a short piece of content.");
      return;
    }
    setBusy(true);
    const t = toast.loading("MAKAI is reviewing this content…");
    try {
      const r = await run({
        data: {
          schoolId,
          contentKind: "other",
          title: title.trim(),
          scope,
          text,
          mediaUrls: media.split(/[\s,]+/).filter(Boolean).slice(0, 12),
          hasAltText: false,
          gradeLevel: grade || undefined,
          subject: subject || undefined,
        },
      });
      toast.success(`Score ${r.overall_score}/100 — ${STATUS_LABEL[r.status] ?? r.status}`, { id: t });
      setOpen(false);
      setTitle(""); setText(""); setMedia("");
      onDone();
    } catch (e) {
      toast.error((e as Error).message, { id: t });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm"><Sparkles className="mr-1.5 h-4 w-4" />Review content</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Review new content</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2">
              <Label>Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. P6 Science notes — Digestion" />
            </div>
            <div className="space-y-1.5">
              <Label>Class / grade</Label>
              <Input value={grade} onChange={(e) => setGrade(e.target.value)} placeholder="e.g. P6" />
            </div>
            <div className="space-y-1.5">
              <Label>Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Science" />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Publishing scope</Label>
              <Select value={scope} onValueChange={(v) => setScope(v as never)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="school">School content</SelectItem>
                  <SelectItem value="marketplace">Marketplace content (stricter)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className="flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" />Media links (optional)</Label>
              <Input value={media} onChange={(e) => setMedia(e.target.value)} placeholder="https://… https://…" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Content text</Label>
            <Textarea rows={10} value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Paste the lesson notes, worksheet text or resource description…" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Run quality review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
